import { useState } from "react";
import { toast } from "sonner";
import { Globe, Check, X } from "lucide-react";
import { updateFavorite } from "../lib/api";

const CURRENCY_SYMBOL = {
  "CA-ON": "C$",
  US: "$",
  UK: "£",
  EU: "€",
};

/**
 * Per-region price override editor for a single favorite.
 * Default placeholder = product.selling_price_usd. Empty value = use default.
 */
export default function RegionPricingEditor({ favorite, onUpdated, rank, regions }) {
  const [editing, setEditing] = useState(false);
  const defaultPrice = favorite.product?.selling_price_usd || 0;
  const [prices, setPrices] = useState(() => {
    const rp = favorite.region_prices || {};
    const out = {};
    for (const r of regions) {
      out[r.code] = rp[r.code] !== undefined ? String(rp[r.code]) : "";
    }
    return out;
  });
  const [saving, setSaving] = useState(false);

  const setPrice = (code, val) =>
    setPrices((p) => ({ ...p, [code]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const region_prices = {};
      for (const [code, v] of Object.entries(prices)) {
        const num = parseFloat(v);
        if (!Number.isNaN(num) && num > 0) {
          region_prices[code] = num;
        }
      }
      const updated = await updateFavorite(favorite.id, { region_prices });
      toast.success("Region prices saved");
      setEditing(false);
      onUpdated?.(updated);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const overrideCount = Object.values(favorite.region_prices || {}).filter(
    (v) => v && v > 0,
  ).length;

  if (!editing) {
    return (
      <div className="mt-3 pt-3 border-t border-zinc-100" data-testid={`region-pricing-${rank}`}>
        <button
          onClick={() => setEditing(true)}
          data-testid={`open-region-pricing-${rank}`}
          className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1"
        >
          <Globe size={10} strokeWidth={2.5} />
          {overrideCount > 0
            ? `Region prices · ${overrideCount} override${overrideCount > 1 ? "s" : ""}`
            : "Set per-region prices"}
        </button>
        {overrideCount > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {regions.map((r) => {
              const v = (favorite.region_prices || {})[r.code];
              if (!v) return null;
              return (
                <span
                  key={r.code}
                  className="text-[9px] font-mono px-2 py-0.5 bg-zinc-100 border border-zinc-200"
                >
                  {r.code}: {CURRENCY_SYMBOL[r.code] || "$"}
                  {v.toFixed(2)}
                </span>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="mt-3 p-3 border border-zinc-900 bg-zinc-50"
      data-testid={`region-pricing-open-${rank}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Globe size={12} strokeWidth={2.5} />
        <span className="micro-label text-[9px]">Per-Region Prices (USD)</span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {regions.map((r) => (
          <div key={r.code}>
            <label className="micro-label block mb-1 text-[9px]">
              {r.code} <span className="text-zinc-400 normal-case">{r.label}</span>
            </label>
            <div className="flex items-center border border-zinc-300 bg-white">
              <span className="px-2 text-zinc-400 font-mono text-xs">
                {CURRENCY_SYMBOL[r.code] || "$"}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={prices[r.code]}
                onChange={(e) => setPrice(r.code, e.target.value)}
                placeholder={defaultPrice.toFixed(2)}
                data-testid={`region-price-input-${r.code}-${rank}`}
                className="w-full px-1 py-1.5 font-mono text-xs focus:outline-none"
              />
            </div>
          </div>
        ))}
      </div>
      <p className="text-[9px] text-zinc-500 mb-3 leading-relaxed">
        Empty = use default ${defaultPrice.toFixed(2)}. Values are USD-equivalent prices charged
        to that region.
      </p>
      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid={`save-region-pricing-${rank}`}
          className="flex-1 bg-[#002FA7] text-white text-[10px] uppercase tracking-widest font-bold py-2 px-3 inline-flex items-center justify-center gap-1 hover:bg-[#001f70] disabled:opacity-50"
        >
          <Check size={12} strokeWidth={3} /> SAVE
        </button>
        <button
          onClick={() => setEditing(false)}
          data-testid={`cancel-region-pricing-${rank}`}
          className="px-3 border border-zinc-900 text-[10px] uppercase tracking-widest font-bold hover:bg-zinc-100"
        >
          <X size={12} strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}
